import { motion, useReducedMotion as useFMReducedMotion, useScroll } from "framer-motion";
import { memo, useEffect, useState } from "react";

// Animation timing constants
export const ANIMATION_TIMING = {
  durations: {
    fast: 0.1,
    normal: 0.4,
    slow: 1.2,
    verySlow: 2.0,
  },
  delays: {
    none: 0,
    short: 0.1,
    medium: 0.3,
    long: 0.5,
    extraLong: 0.8,
  },
};

// Hook for reduced motion preference using framer-motion's built-in hook
export const useReducedMotion = () => {
  return useFMReducedMotion();
};

// Accessibility-friendly animation wrapper
export const AccessibleMotion = memo(({
  children,
  reducedMotion = false,
  ...motionProps
}) => {
  const prefersReducedMotion = useReducedMotion();

  // If user prefers reduced motion, use simple fade-in
  if (prefersReducedMotion || reducedMotion) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: ANIMATION_TIMING.durations.fast }}
        {...motionProps}
      >
        {children}
      </motion.div>
    );
  }

  return (
    <motion.div {...motionProps}>
      {children}
    </motion.div>
  );
});

// Responsive animation presets that adapt to motion preferences
export const getResponsiveAnimation = (animationType, options = {}) => {
  const { prefersReducedMotion } = options;

  const baseAnimations = {
    fadeInUp: {
      initial: { opacity: 0, y: 20 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: -20 },
    },
    fadeIn: {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
    },
    slideInLeft: {
      initial: { opacity: 0, x: -50 },
      animate: { opacity: 1, x: 0 },
      exit: { opacity: 0, x: -50 },
    },
  };

  const animation = baseAnimations[animationType];
  if (!animation) return baseAnimations.fadeInUp;

  // Modify animations based on preferences
  if (prefersReducedMotion) {
    return {
      ...animation,
      transition: {
        duration: ANIMATION_TIMING.durations.fast,
        ease: EASING_CURVES.smooth,
      },
    };
  }

  return {
    ...animation,
    transition: {
      duration: ANIMATION_TIMING.durations.normal,
      ease: EASING_CURVES.smooth,
    },
  };
};

// Easing curves for consistent motion
export const EASING_CURVES = {
 smooth: [0.25, 0.46, 0.45, 0.94],
 bounce: [0.68, -0.55, 0.265, 1.55],
 sharp: [0.4, 0, 0.2, 1],
 gentle: [0.25, 0.1, 0.25, 1],
};

// Animation presets for common patterns
export const ANIMATION_PRESETS = {
 fadeInUp: {
   initial: { opacity: 0, y: 20 },
   animate: { opacity: 1, y: 0 },
   exit: { opacity: 0, y: -20 },
 },
 fadeInDown: {
   initial: { opacity: 0, y: -20 },
   animate: { opacity: 1, y: 0 },
   exit: { opacity: 0, y: 20 },
 },
 fadeIn: {
   initial: { opacity: 0 },
   animate: { opacity: 1 },
   exit: { opacity: 0 },
 },
 scaleIn: {
   initial: { opacity: 0, scale: 0.8 },
   animate: { opacity: 1, scale: 1 },
   exit: { opacity: 0, scale: 0.8 },
 },
 slideInLeft: {
   initial: { opacity: 0, x: -50 },
   animate: { opacity: 1, x: 0 },
   exit: { opacity: 0, x: -50 },
 },
 slideInRight: {
   initial: { opacity: 0, x: 50 },
   animate: { opacity: 1, x: 0 },
   exit: { opacity: 0, x: 50 },
 },
};

// Scroll animation presets
export const SCROLL_PRESETS = {
 fadeInOnScroll: {
   initial: { opacity: 0 },
   whileInView: { opacity: 1 },
   viewport: { once: true, margin: "-100px" },
 },
 slideUpOnScroll: {
   initial: { opacity: 0, y: 50 },
   whileInView: { opacity: 1, y: 0 },
   viewport: { once: true, margin: "-50px" },
 },
 scaleOnScroll: {
   initial: { opacity: 0, scale: 0.8 },
   whileInView: { opacity: 1, scale: 1 },
   viewport: { once: true, margin: "-100px" },
 },
};

// Transform configuration helper
export const getTransformOptions = (options = {}) => ({
  ease: EASING_CURVES.smooth,
  ...options,
});

// Staggered animation helper
export const createStaggerDelay = (index, baseDelay = 0.1, multiplier = 1) => {
 return baseDelay * index * multiplier;
};

// Performance-optimized motion component wrapper
export const OptimizedMotionDiv = memo(({ children, style, className, ...motionProps }) => (
 <motion.div
   style={{
     willChange: 'transform, opacity',
     ...style,
   }}
   className={className}
   {...motionProps}
 >
   {children}
 </motion.div>
));

// Scroll reveal component for consistent scroll animations
export const ScrollReveal = memo(({
 children,
 delay = 0,
 duration = ANIMATION_TIMING.durations.normal,
 easing = EASING_CURVES.smooth,
 direction = 'up',
 className,
 ...props
}) => {
 const getInitialProps = () => {
   switch (direction) {
     case 'up':
       return { opacity: 0, y: 50 };
     case 'down':
       return { opacity: 0, y: -50 };
     case 'left':
       return { opacity: 0, x: -50 };
     case 'right':
       return { opacity: 0, x: 50 };
     default:
       return { opacity: 0 };
   }
 };

 return (
   <motion.div
     initial={getInitialProps()}
     whileInView={{ opacity: 1, x: 0, y: 0 }}
     transition={{ delay, duration, ease: easing }}
     viewport={{ once: true, margin: "-100px" }}
     className={className}
     style={{ willChange: 'transform, opacity' }}
     {...props}
   >
     {children}
   </motion.div>
 );
});

// Legacy CSS variable helper (maintained for compatibility)
const stylesWithCssVar = (styles) => {
 const cssVarStyles = {};
 for (const key in styles) {
   if (key.startsWith("--")) {
     cssVarStyles[key] = styles[key];
   }
 }
 return <motion.div style={{ ...styles, ...cssVarStyles }} />;
};

export default stylesWithCssVar;